import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { PageFooter } from '@martinus/styles/client/PageFooter.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import { Permission } from '../../../permissions/roles.js';
import { Person } from '../../../users/entities/person.js';
import { formatValue, outOfSyncRows } from '../../api/entraComparison.js';
import { MicrosoftAPI } from '../../api/MicrosoftAPI.js';
import { isAccountEnabledInEntra } from '../../api/microsoftAccountLifecycle.js';
import { MicrosoftUserState } from '../../entities/MicrosoftUserState.js';
import { type EntraComparisonRow, EntraComparisonTable, EntraSyncBadge } from '../entra-comparison.js';
import { SgMicrosoftEmailAliasEditDialog } from './sg-microsoft-email-alias-edit-dialog.js';
import { MicrosoftUserNotInEntra } from './sg-microsoft-entity-no-in-entra.js';
import {
  MICROSOFT_TAB_LAYOUT,
  MicrosoftSyncIndicator,
  type MicrosoftSyncStatus,
} from './sg-microsoft-sync-indicator.js';

const USER_SELECTION_SET = {
  _id: 1,
  roles: { name: 1, contexts: 1 },
  debitorennummer: 1,
  username: 1,
  microsoftUserData: {
    microsoftID: 1,
    shareWithMicrosoft: 1,
    userPrincipalName: 1,
    otherMails: 1,
    manualMails: 1,
    syncRejections: { field: 1, value: 1, code: 1, message: 1, rejectedAt: 1 },
    createdInEntra: 1,
    mail: 1,
    manuallyChangedName: 1,
  },
  profilDaten: { stammdaten: { vorname: 1, nachname: 1 } },
} as const;

type MicrosoftPerson = NarrowedEntity<Person, typeof USER_SELECTION_SET>;

/**
 * The snapshot fields the comparison reads, declared structurally rather than as the entity: the
 * subscription hands back a narrowed entity, and naming the fields here keeps it obvious which parts of
 * a snapshot the tab actually depends on.
 */
type MicrosoftUserSnapshot = Pick<
  MicrosoftUserState,
  | 'microsoftID'
  | 'createdInEntra'
  | 'principalName'
  | 'sharedData'
  | 'accountEnabled'
  | 'firstName'
  | 'lastName'
  | 'displayName'
  | 'inGroups'
  | 'email'
  | 'otherEmails'
  | 'proxyAddresses'
>;

/** What the Entra card needs beyond the compared values: when the snapshot was taken. */
type MicrosoftUserStateView = MicrosoftUserSnapshot & Pick<MicrosoftUserState, 'createdAt'>;

type MicrosoftUserDataView = NonNullable<MicrosoftPerson['microsoftUserData']>;

/**
 * Entra prefixes a proxy address with `SMTP:` for the primary and `smtp:` for the rest. The prefix is
 * how the two are told apart, and nothing to compare an address against.
 * @param address one entry of `proxyAddresses`
 */
const stripProxyPrefix = (address: string) => address.replace(/^smtp:/i, '');

/** Every address this person should be reachable under: their primary plus all their aliases. */
const deliverableAddressesOf = (local: Maybe<MicrosoftUserDataView>) =>
  [local?.mail, ...(local?.otherMails ?? [])].filter(isNotNullish).filter(Boolean);

/** The aliases that came out of the person's roles, as opposed to the ones somebody typed. */
const derivedAliasesOf = (local: Maybe<MicrosoftUserDataView>) => {
  const manual = new Set((local?.manualMails ?? []).map(address => address?.toLowerCase()));

  return (local?.otherMails ?? []).filter(address => !manual.has(address?.toLowerCase()));
};

/**
 * What Entra refused, and why.
 *
 * A refusal that is only a row in a log collection is a refusal nobody acts on - which is how the same
 * four requests went out every night for over a week. Naming the value and quoting Microsoft's own
 * message puts it in front of the person who can free the address or grant the role.
 * @param rejections the refusals recorded on this person
 */
const RejectionNotice = (rejections: Maybe<MicrosoftUserDataView['syncRejections']>) => {
  if (!rejections?.length) return nothing;

  return html`
    <div class="col-span-2 mt-2 flex flex-col gap-2 rounded-box border border-error/40 bg-error/5 p-3">
      <p class="flex items-center gap-2 text-sm font-semibold text-error">
        <span class="icon-[material-symbols--error-outline-rounded] text-lg"></span>
        Von Entra abgelehnt
      </p>
      ${repeat(
        rejections.filter(isNotNullish),
        rejection => `${rejection.field}${rejection.value}`,
        rejection => html`
          <p class="text-sm text-base-content/80">
            <span class="font-semibold">${rejection.field}</span> = ${formatValue(rejection.value)}<br />
            <span class="text-base-content/70">${rejection.message ?? rejection.code}</span>
          </p>
        `,
      )}
      <p class="text-sm text-base-content/70">
        Dieser Wert wird nicht erneut gesendet, bis er sich ändert. Die Ursache liegt in Entra – eine Adresse, die
        bereits einem anderen Objekt gehört, oder ein Konto, für das diese Anwendung keine Berechtigung hat.
      </p>
    </div>
  `;
};

/**
 * Every value the person has on both sides.
 *
 * ! Everything the tab shows about sync state is derived from this list. !
 * Adding a value here adds its row, its checkmark and its effect on the header badge and the repair
 * button in one go - there is no second place that has to be kept in agreement.
 * @param person the person as our database holds them
 * @param state the snapshot of what Entra holds
 * @param desiredGroups the groups the person belongs to, named and counted by the server
 */
function buildUserComparisonRows(
  person: Maybe<MicrosoftPerson>,
  state: Maybe<MicrosoftUserSnapshot>,
  desiredGroups: string[],
): EntraComparisonRow[] {
  const local = person?.microsoftUserData;
  const stammdaten = person?.profilDaten?.stammdaten;

  const rows: EntraComparisonRow[] = [
    { label: 'MicrosoftID', local: local?.microsoftID, entra: state?.microsoftID },
    { label: 'In Entra erstellt am', local: local?.createdInEntra, entra: state?.createdInEntra },
    { label: 'Nutzer-Principal-Name (Login)', local: local?.userPrincipalName, entra: state?.principalName },
    { label: 'Daten geteilt', local: !!local?.shareWithMicrosoft, entra: !!state?.sharedData },
    { label: 'Konto aktiv', local: !!person && isAccountEnabledInEntra(person), entra: state?.accountEnabled },
  ];

  // the real name only reaches Entra once the person agreed to share it, so before that there is
  // nothing to compare and the display name is the anonymous debitorennummer
  if (local?.shareWithMicrosoft) {
    rows.push(
      { label: 'Vorname', local: stammdaten?.vorname, entra: state?.firstName },
      { label: 'Nachname', local: stammdaten?.nachname, entra: state?.lastName },
      {
        label: 'Anzeigename',
        local: `${stammdaten?.vorname ?? ''} ${stammdaten?.nachname ?? ''}`.trim(),
        entra: state?.displayName,
      },
    );
  } else {
    rows.push({ label: 'Anzeigename', local: person?.debitorennummer, entra: state?.displayName });
  }

  rows.push(
    {
      // ! Both sides of this row come from the server. !
      //
      // Our side used to be read off the person's bookings, and a booking resolves its groups through
      // the publish filter - so a group in a school the person looking may not read was missing from
      // our side and the row reported a difference that only they could see. It also compared the bare
      // group name against the name Entra holds, which for a school class carries the school in front
      // of it, so every class was permanently out of sync with itself.
      label: 'In Gruppen',
      local: desiredGroups,
      entra: [...(state?.inGroups ?? [])].sort((a, b) => a.localeCompare(b)),
    },
    { label: 'E-Mailadresse', local: local?.mail, entra: state?.email },
    {
      // ! Shown, never counted. ! These are the addresses that actually deliver, and Graph refuses to
      // write them - so a difference here is a job for the Exchange admin centre, not for the repair
      // button. Counting it would paint the card red with nothing anybody here could do about it.
      label: 'Zustellbare Adressen',
      local: deliverableAddressesOf(local),
      entra: (state?.proxyAddresses ?? []).map(stripProxyPrefix),
      informational: true,
    },
    {
      // Entra's own contact field. We stopped writing it - it delivers nothing and it is where
      // self-service password reset takes a person's alternate address from.
      label: 'Kontaktadressen in Entra',
      local: state?.otherEmails ?? [],
      entra: state?.otherEmails ?? [],
      informational: true,
    },
  );

  return rows;
}

/** One label/value row of the portal card. Kept flat so the two cards line up visually. */
const portalRow = (label: string, value: unknown) => html`
  <dt class="text-sm font-semibold text-base-content/70">${label}</dt>
  <dd class="min-w-0 text-base-content">${formatValue(value)}</dd>
`;

/**
 * The left card: what our database holds, and the few values that can be edited here.
 *
 * Our database is the master record, so this side is never marked right or wrong - it *is* the target.
 * The old version rendered invisible checkmarks here purely to make the rows line up with the card on
 * the right; the two cards now simply share a row rhythm.
 */
const PortalCard = stateful((props: { user: Maybe<MicrosoftPerson>; groups: string[] }) => {
  const [shared, setShared] = useState(props.user?.microsoftUserData?.shareWithMicrosoft);
  const [manual, setManual] = useState(props.user?.microsoftUserData?.manuallyChangedName);
  const _currentUser = useObservable(() => currentUser(), []);

  const user = props.user;
  const mayEdit = !!user && !!_currentUser?.hasPermission(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT);
  const stammdaten = user?.profilDaten?.stammdaten;
  const local = user?.microsoftUserData;

  return html`
    <div class="card card-body flex-1">
      ${CardHeader({ title: 'Im Portal', subtitle: 'Diese Werte sind maßgeblich und werden nach Entra übertragen.' })}

      <dl class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-x-3 gap-y-2">
        ${portalRow('MicrosoftID', local?.microsoftID)} ${portalRow('In Entra erstellt am', local?.createdInEntra)}

        <dt class="text-sm font-semibold text-base-content/70">Nutzer-Principal-Name (Login)</dt>
        <dd class="flex min-w-0 items-center gap-2 text-base-content">
          ${
            manual && mayEdit
              ? html`
                  ${TextInput({
                    value: local?.userPrincipalName?.split('@')[0],
                    onPicked: localPart => {
                      if (!props.user?.microsoftUserData) return;
                      const domain = props.user.microsoftUserData.userPrincipalName?.split('@')[1];
                      props.user.microsoftUserData.userPrincipalName = `${localPart ?? ''}@${domain}`;
                    },
                  })}
                  <span class="shrink-0">@${local?.userPrincipalName?.split('@')[1]}</span>
                `
              : html`<span class="truncate">${formatValue(local?.userPrincipalName)}</span>`
          }
          ${
            mayEdit
              ? html`
                  <div
                    class="tooltip tooltip-left shrink-0"
                    data-tip="Manuellen Nutzernamen setzen? Dadurch wird die automatische Erzeugung deaktiviert."
                    ${hoverTooltip('left')}
                  >
                    ${ToggleInput({
                      value: manual,
                      onPicked: value => {
                        if (!props.user?.microsoftUserData) return;
                        props.user.microsoftUserData.manuallyChangedName = !!value;
                        setManual(!!value);
                      },
                    })}
                  </div>
                `
              : nothing
          }
        </dd>

        <dt class="text-sm font-semibold text-base-content/70">Daten mit Microsoft teilen?</dt>
        <dd class="min-w-0">
          <div
            class="tooltip tooltip-left"
            data-tip="Hiermit werden die persönlichen Daten (Vor- und Nachname) mit Microsoft geteilt. Dies ermöglicht es die Person in Microsoft mit Klarnamen zu finden und zu verwalten."
            ${hoverTooltip('left')}
          >
            ${ToggleInput({
              disabled: !!shared && !mayEdit,
              value: shared,
              onPicked: async () => {
                if (!props.user?.microsoftUserData) return;
                if (props.user.microsoftUserData.shareWithMicrosoft) return;

                // as we need to build emails with the vorname and nachname, we need to check if they are set
                if (!stammdaten?.vorname || !stammdaten.nachname) {
                  await alertDialog('Bitte setzen Sie den Vor- und Nachnamen im Profil.');
                  props.user.microsoftUserData.shareWithMicrosoft = false;
                  setShared(false);
                  return;
                }

                if (!(await confirmDialog('Daten mit Microsoft teilen?'))) {
                  props.user.microsoftUserData.shareWithMicrosoft = false;
                  setShared(false);
                  return;
                }

                props.user.microsoftUserData.shareWithMicrosoft = true;
                setShared(true);
                const user = props.user;
                await guardDialog(() => user.save());
              },
            })}
          </div>
        </dd>

        ${
          local?.shareWithMicrosoft
            ? html`
                ${portalRow('Vorname', stammdaten?.vorname)} ${portalRow('Nachname', stammdaten?.nachname)}
                ${portalRow('Anzeigename', `${stammdaten?.vorname ?? ''} ${stammdaten?.nachname ?? ''}`.trim())}
              `
            : portalRow('Anzeigename', props.user?.debitorennummer)
        }

        <dt class="text-sm font-semibold text-base-content/70">In Gruppen</dt>
        <dd class="min-w-0 text-base-content">${props.groups.length ? formatValue(props.groups) : 'Keine Gruppen'}</dd>

        ${portalRow('E-Mailadresse', local?.mail)} ${portalRow('Aus Rollen erzeugt', derivedAliasesOf(local))}

        <dt class="text-sm font-semibold text-base-content/70">Zusätzliche Adressen</dt>
        <dd class="flex min-w-0 items-center gap-2 text-base-content">
          <span class="min-w-0 truncate">${formatValue(local?.manualMails ?? [])}</span>
          ${
            props.user && _currentUser?.hasPermission(Permission.CHANGE_EMAIL_ALIAS)
              ? SgMicrosoftEmailAliasEditDialog({ user: props.user })
              : nothing
          }
        </dd>

        ${RejectionNotice(local?.syncRejections)}
      </dl>
    </div>
  `;
});

/**
 * The right card: what Entra holds, and how it differs.
 *
 * The header badge, the per-row checkmarks and the repair button all read the same row list, so they
 * cannot contradict each other. They used to: the badge ran its own comparison which looked at a field
 * (`gruppen`) that does not exist on a person, so it was always false for anybody in at least one Entra
 * group - the card stayed red while every row in it showed a green check.
 */
const EntraCard = stateful(
  (props: {
    userID: Maybe<string>;
    state: Maybe<MicrosoftUserStateView>;
    rows: EntraComparisonRow[];
    differences: EntraComparisonRow[];
    loading: boolean;
  }) => {
    const _currentUser = useObservable(() => currentUser(), []);

    const state = props.state;
    const userID = props.userID;
    const mayRepair = !!userID && !!_currentUser?.hasPermission(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT);

    return html`
      <div
        class="card card-body flex-1 ${props.loading || !props.differences.length ? '' : 'border-error/40 bg-error/5'}"
      >
        ${CardHeader({
          title: html`<span class="flex items-center gap-2">Entra ${EntraSyncBadge(props.rows, props.loading)}</span>`,
          subtitle: state?.createdAt ? html`Stand: ${formatValue(state.createdAt)}` : 'Noch kein Abgleich',
          actionsDivider: true,
          actions: html`
            <button
              class="btn btn-ghost btn-sm btn-square"
              title="Status neu von Entra laden"
              ?disabled=${!userID}
              @click=${() => guardDialog(() => MicrosoftAPI.refreshStateForUser(userID ?? '')())}
            >
              <span class="icon-[material-symbols--refresh]"></span>
            </button>
          `,
        })}
        ${EntraComparisonTable(props.rows, props.loading)}
        ${
          mayRepair
            ? html`
                <div class="mt-auto flex flex-col gap-2">
                  ${
                    !props.loading && props.differences.length
                      ? html`
                          <button
                            class="btn btn-primary btn-block"
                            @click=${() => guardDialog(() => MicrosoftAPI.repairUser(userID ?? '')())}
                          >
                            <span class="icon-[material-symbols--build]"></span>
                            Nutzer reparieren
                          </button>
                        `
                      : nothing
                  }
                  <button
                    class="btn btn-outline btn-block"
                    @click=${() =>
                      guardDialog(() => MicrosoftAPI.resetUserPassword(userID ?? '')(), {
                        hasToConfirm: true,
                        confirmText:
                          'Passwort zurücksetzen? Das bisherige Passwort wird ungültig und die Person erhält ein Einmalpasswort per E-Mail.',
                        acceptText: 'Zurücksetzen',
                        successText: 'Einmalpasswort wurde per E-Mail gesendet',
                        errorMessagePrefix: 'Passwort zurücksetzen',
                      })}
                  >
                    <span class="icon-[material-symbols--lock-reset]"></span>
                    Passwort zurücksetzen
                  </button>
                </div>
              `
            : nothing
        }
      </div>
    `;
  },
);

export const SgMicrosoftUserControl = stateful((props: { userID: Maybe<string> }) => {
  const _currentUser = useObservable(() => currentUser(), []);
  const user = useQuery(
    () => (props.userID ? subscribeByID(Person, props.userID)(USER_SELECTION_SET) : of(null)),
    [props.userID],
  );

  // one subscription for the whole tab: the card and the indicator between the cards have to answer
  // the same question, so they read the same rows rather than each deriving their own
  const { value: state, loading: stateLoading } = useQuery(
    () =>
      props.userID
        ? MicrosoftAPI.subscribeStateForUser(props.userID)(selectionSet(() => MicrosoftUserState, 1))
        : of(null),
    [props.userID],
  );

  // ! Our own side of the group comparison is asked for, not worked out here. !
  // The person subscription only ever hands back the groups this account may see, and comparing a
  // filtered list against Entra's unfiltered one reports a difference nobody but the caller has - and
  // offers a repair button for it. @see resolveGetDesiredEntraGroupsForPerson
  const { value: desiredGroups, loading: groupsLoading } = useQuery<string[]>(
    () => (props.userID ? MicrosoftAPI.getDesiredGroupsForUser(props.userID)() : Promise.resolve([])),
    [props.userID],
  );

  if (user.loading) return PageLoader({ cardOptions: [{ title: 'Im Portal' }, { title: 'Entra' }] });

  if (user.value && !user.value.microsoftUserData?.microsoftID) return MicrosoftUserNotInEntra({ user: user.value });

  const groups = desiredGroups ?? [];
  const comparisonLoading = stateLoading || groupsLoading;
  const rows = buildUserComparisonRows(user.value, state, groups);
  const differences = outOfSyncRows(rows);
  const status: MicrosoftSyncStatus = comparisonLoading ? 'loading' : differences.length ? 'drift' : 'synced';

  const savedUser = user.value;
  const mayEdit = !!savedUser && !!_currentUser?.hasPermission(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT);

  /**
   * ! The save bar is a sibling of the card container, never a child of a card. !
   *
   * It is `position: fixed` and sized with `-webkit-fill-available`, which resolves against its
   * containing block - nested inside a card it took on the card's width and sat inside the card
   * container's margin instead of spanning the viewport. Every other page in this app renders it the
   * same way, next to the container rather than inside the content.
   */
  return html`
    <div class="card-container ${MICROSOFT_TAB_LAYOUT}">
      ${PortalCard({ user: user.value, groups })} ${MicrosoftSyncIndicator({ status, differences: differences.length })}
      ${EntraCard({ userID: props.userID, state, rows, differences, loading: comparisonLoading })}
    </div>

    ${
      mayEdit
        ? PageFooter({
            content: html`
              <span class="text-sm text-base-content/70">Änderungen werden nach dem Speichern übertragen.</span>
              <button class="btn btn-primary btn-lg" @click=${() => guardDialog(() => savedUser.save())}>
                <span class="icon-[material-symbols--save]"></span>
                Speichern
              </button>
            `,
          })
        : nothing
    }
  `;
});
